// src/components/core/data/DataStateHandler.jsx
import React from 'react';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';

/**
 * Handles loading, error, and empty states consistently across the app
 * @param {Object} props - Component props
 * @param {boolean} props.isLoading - Whether data is loading
 * @param {string|null} props.error - Error message if any
 * @param {boolean} props.isEmpty - Whether data is empty
 * @param {string} props.emptyMessage - Message to display when data is empty
 * @param {string} props.errorMessage - Custom error message
 * @param {Function} props.onRetry - Function to retry loading data
 * @param {React.ReactNode} props.loadingComponent - Custom loading component
 * @param {React.ReactNode} props.emptyComponent - Custom empty state component
 * @param {React.ReactNode} props.errorComponent - Custom error component
 * @param {React.ReactNode} props.children - Content to display when data is loaded
 */
const DataStateHandler = ({
  isLoading,
  error,
  isEmpty,
  emptyMessage = "No data found",
  errorMessage,
  onRetry,
  loadingComponent,
  emptyComponent,
  errorComponent,
  children
}) => {
  if (isLoading) {
    return loadingComponent || (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return errorComponent || (
      <Alert 
        severity="error" 
        action={onRetry && (
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        )}
      >
        {errorMessage || error}
      </Alert>
    );
  }
  
  if (isEmpty) {
    return emptyComponent || (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }
  
  return children;
};

export default DataStateHandler;